import Image from "next/image"
import { Badge } from "@/components/ui/badge"
import { ProductsSearchDataResponse } from "@repo/api-shared/src/endpoints"
import { useMemo } from "react"
import { Button } from "@/components/ui/button"
import { FastLink } from "../ui/fast-link"
import { StorefrontRoutesMap } from "@repo/api-shared"

export function ProductCard({
    title,
    handle,
    thumbnail,
    categories,
    sizes,
    prices,
    selectedPriceRange,
}: ProductsSearchDataResponse["products"][string] & {
    selectedPriceRange?: [number, number]
}) {
    const { price, originalPrice, discountPercentage } = useMemo(() => {
        let minPrice: number | null = null
        let correspondingOriginalPrice: number | null = null
        let maxDiscount = 0

        for (const price of prices) {
            if (!minPrice || price.calculated_amount < minPrice) {
                minPrice = price.calculated_amount
                correspondingOriginalPrice = price.original_amount

                if (price.original_amount > price.calculated_amount) {
                    const discount = Math.round(
                        ((price.original_amount - price.calculated_amount) /
                            price.original_amount) *
                            100,
                    )
                    maxDiscount = Math.max(maxDiscount, discount)
                }
            }
        }

        return {
            price: minPrice,
            originalPrice: correspondingOriginalPrice,
            discountPercentage: maxDiscount,
        }
    }, [prices])

    const isPriceLessThanMinPrice = useMemo(() => {
        return price && selectedPriceRange && price < selectedPriceRange[0]
    }, [price, selectedPriceRange])

    const uniqueSizes = useMemo(() => {
        const sizeMap = new Map()
        for (const size of sizes) {
            const key = `${size.dimensions.width}x${size.dimensions.length}`
            if (
                !sizeMap.has(key) ||
                (size.calculated_price &&
                    (!sizeMap.get(key).calculated_price ||
                        size.calculated_price <
                            sizeMap.get(key).calculated_price))
            ) {
                sizeMap.set(key, size)
            }
        }
        return Array.from(sizeMap.values()).sort((a, b) => {
            // First compare by width
            if (a.dimensions.width !== b.dimensions.width) {
                return a.dimensions.width - b.dimensions.width
            }
            // If widths are equal, compare by length
            return a.dimensions.length - b.dimensions.length
        })
    }, [sizes])

    return (
        <FastLink
            prefetch={true}
            href={`${StorefrontRoutesMap.PRODUCT_TEMPLATE}/${handle}`}
        >
            <div className="flex h-full flex-col border border-gray-300 transition-all hover:border-primary">
                <div className="relative">
                    <Image
                        src={thumbnail || "/placeholder.svg"}
                        alt={title}
                        width={400}
                        height={300}
                        className="h-48 w-full object-cover"
                    />
                    {categories.map((category) => (
                        <Badge
                            key={category}
                            className="absolute right-2 top-2 bg-primary"
                        >
                            {category}
                        </Badge>
                    ))}
                </div>

                <div className="flex flex-1 flex-col justify-between gap-2 p-4">
                    <div>
                        <h3 className="line-clamp-2 font-medium">{title}</h3>

                        <div className="mt-2 flex flex-wrap gap-1">
                            {uniqueSizes.slice(0, 3).map((sizeData) => (
                                <Badge
                                    key={`${sizeData.dimensions.width}x${sizeData.dimensions.length}`}
                                    variant="outline"
                                >
                                    {sizeData.dimensions.width}x
                                    {sizeData.dimensions.length}м
                                </Badge>
                            ))}
                            {uniqueSizes.length > 3 && (
                                <Badge variant="outline">
                                    +{uniqueSizes.length - 3}
                                </Badge>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="flex flex-col items-start gap-1">
                            {originalPrice &&
                                price &&
                                originalPrice > price && (
                                    <div className="text-sm text-gray-500 line-through">
                                        {originalPrice.toLocaleString("ru-RU")}{" "}
                                        ₽
                                    </div>
                                )}
                            <div className="flex items-center gap-2">
                                <span className="text-xl font-bold text-primary">
                                    {isPriceLessThanMinPrice && "от "}
                                    {price?.toLocaleString("ru-RU") || "-"} ₽
                                </span>
                                {discountPercentage > 0 && (
                                    <span className="text-sm font-medium text-green-600">
                                        (-{discountPercentage}%)
                                    </span>
                                )}
                            </div>
                        </div>

                        <Button className="mt-2 w-full">
                            {categories.includes("Бытовки")
                                ? "Выбрать размеры"
                                : categories.includes("Дачные домики")
                                  ? "Выбрать опции"
                                  : "Подробнее"}
                        </Button>
                    </div>
                </div>
            </div>
        </FastLink>
    )
}
